'use client'

import React, { createContext, useContext, useMemo, useRef } from 'react'
import {
  Box,
  RadioGroup,
  Separator,
  Slider,
  Switch as ChakraSwitch,
  Table,
  Tabs,
  InputAddon,
} from '@chakra-ui/react'

const AutoValueContext = createContext(null)

function useAutoValue(explicit) {
  const ctx = useContext(AutoValueContext)
  const stored = useRef(null)
  if (explicit != null && explicit !== '') return String(explicit)
  if (stored.current == null) {
    stored.current = String(ctx ? ctx.alloc() : 0)
  }
  return stored.current
}

function AutoValueProvider({ children }) {
  const api = useMemo(() => {
    let n = 0
    return { alloc: () => n++ }
  }, [])
  return <AutoValueContext.Provider value={api}>{children}</AutoValueContext.Provider>
}

export const Divider = Separator

export function TabList(props) {
  return (
    <AutoValueProvider>
      <Tabs.List {...props} />
    </AutoValueProvider>
  )
}

export function Tab({ value, ...props }) {
  return <Tabs.Trigger value={useAutoValue(value)} {...props} />
}

export function TabPanels(props) {
  return (
    <AutoValueProvider>
      <Box {...props} />
    </AutoValueProvider>
  )
}

export function TabPanel({ value, ...props }) {
  return <Tabs.Content value={useAutoValue(value)} {...props} />
}

/** Chakra v3 Tabs: `value`/`onValueChange` son string; muchos sitios aún usan índices number. */
export function tabsControlledValue(value) {
  if (value == null) return undefined
  return String(value)
}

export function tabsOnValueChange(handler) {
  return (details) => {
    if (!handler) return
    const raw = details?.value ?? details
    const asNum = Number(raw)
    if (Number.isInteger(asNum) && String(asNum) === String(raw)) {
      handler(asNum)
      return
    }
    handler(raw)
  }
}

export const Thead = (props) => <Table.Header {...props} />
export const Tbody = (props) => <Table.Body {...props} />
export const Tr = (props) => <Table.Row {...props} />
export const Th = (props) => <Table.ColumnHeader {...props} />
export const Td = (props) => <Table.Cell {...props} />
export const TableContainer = (props) => <Table.ScrollArea {...props} />

export const InputRightAddon = (props) => <InputAddon {...props} />

export function Radio(props) {
  const { children, ...rest } = props
  return (
    <RadioGroup.Item {...rest}>
      <RadioGroup.ItemHiddenInput />
      <RadioGroup.ItemIndicator />
      {children}
    </RadioGroup.Item>
  )
}

/** API estilo Chakra v2: `<Switch checked onChange={(e)=>e.target.checked} />` / `onValueChange`. */
export const Switch = React.forwardRef(function CompatSwitch(props, ref) {
  const {
    checked,
    defaultChecked,
    disabled,
    id,
    name,
    value,
    size,
    colorPalette,
    onChange,
    onValueChange,
    onCheckedChange,
    children,
    ...rest
  } = props

  const emit = (nextChecked) => {
    if (typeof onCheckedChange === 'function') {
      onCheckedChange({ checked: nextChecked })
    }
    if (typeof onValueChange === 'function') {
      // Algunos handlers esperan evento; otros no usan el argumento.
      onValueChange({ checked: nextChecked, target: { checked: nextChecked } })
    }
    if (typeof onChange === 'function') {
      onChange({ target: { checked: nextChecked } })
    }
  }

  return (
    <ChakraSwitch.Root
      ref={ref}
      id={id}
      name={name}
      value={value}
      size={size}
      colorPalette={colorPalette}
      checked={checked}
      defaultChecked={defaultChecked}
      disabled={disabled}
      onCheckedChange={(details) => emit(!!details?.checked)}
      {...rest}
    >
      <ChakraSwitch.HiddenInput />
      <ChakraSwitch.Control>
        <ChakraSwitch.Thumb />
      </ChakraSwitch.Control>
      {children}
    </ChakraSwitch.Root>
  )
})

export const SliderTrack = (props) => <Slider.Track {...props} />
export const SliderFilledTrack = (props) => <Slider.Range {...props} />
export const SliderThumb = ({ index = 0, children, ...props }) => (
  <Slider.Thumb index={index} {...props}>
    {children}
    <Slider.HiddenInput />
  </Slider.Thumb>
)

/**
 * Chakra v3 exige `value` como array y el slot `Control` (ancho de la pista).
 * Esta capa acepta number o number[] y envuelve Track/Thumb en Control.
 */
export function SliderRoot({ value, defaultValue, onValueChange, children, w, width, ...props }) {
  const toArr = (v) => {
    if (v == null) return undefined
    return Array.isArray(v) ? v : [Number(v)]
  }
  return (
    <Slider.Root
      {...props}
      w={w ?? width ?? '100%'}
      width={width ?? w ?? '100%'}
      value={toArr(value)}
      defaultValue={toArr(defaultValue)}
      onValueChange={(details) => {
        if (!onValueChange) return
        const arr = details?.value
        if (Array.isArray(arr)) {
          onValueChange(arr.length <= 1 ? (arr[0] ?? 0) : arr)
          return
        }
        onValueChange(details)
      }}
    >
      <Slider.Control w="100%" width="100%">
        {children}
      </Slider.Control>
    </Slider.Root>
  )
}

export function RangeSlider(props) {
  return <Slider.Root {...props} />
}
export const RangeSliderTrack = (props) => <Slider.Track {...props} />
export const RangeSliderFilledTrack = (props) => <Slider.Range {...props} />
export const RangeSliderThumb = (props) => <Slider.Thumb {...props} />
